export type CropType = 'ble' | 'mais' | 'colza' | 'tournesol' | 'orge' | 'autre';
export type LivestockType = 'bovin_viande' | 'bovin_lait' | 'porcin' | 'ovin' | 'caprin' | 'avicole' | 'autre';
export type SubsidyType = 'pac' | 'maec' | 'aide_regionale' | 'autre';
export type MonthlyPattern = 'uniform' | 'winter_heavy';

export interface AgriCampaign {
  id: string;
  user_id: string;
  name: string;
  crop_type: CropType;
  surface_ha: number;
  target_yield_qtx_ha: number;
  target_price_eur_t: number;
  harvest_date: string | null;
  sowing_date: string | null;
  campaign_year: number;
  status: 'en_cours' | 'recolte' | 'cloturee';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgriCampaignCharge {
  id: string;
  user_id: string;
  campaign_id: string;
  label: string;
  amount: number;
  charge_date: string | null;
  charge_type: 'variable' | 'fixed';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgriSubsidy {
  id: string;
  user_id: string;
  name: string;
  amount: number;
  theoretical_payment_date: string | null;
  prudent_payment_date: string | null;
  subsidy_type: SubsidyType;
  campaign_year: number;
  status: 'attendu' | 'recu' | 'annule';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgriLivestockHerd {
  id: string;
  user_id: string;
  name: string;
  livestock_type: LivestockType;
  total_animals: number;
  cows_count: number;
  heifers_count: number;
  calves_count: number;
  other_count: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgriLivestockSale {
  id: string;
  user_id: string;
  herd_id: string;
  label: string;
  quantity: number;
  unit_price: number;
  planned_date: string | null;
  campaign_year: number;
  status: 'prevu' | 'realise' | 'annule';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgriLivestockCharge {
  id: string;
  user_id: string;
  herd_id: string;
  label: string;
  annual_amount: number;
  monthly_pattern: MonthlyPattern;
  campaign_year: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgriEquipment {
  id: string;
  user_id: string;
  name: string;
  purchase_price: number;
  equity_amount: number;
  financed_amount: number;
  loan_duration_years: number;
  monthly_payment: number;
  purchase_date: string | null;
  status: 'projet' | 'finance' | 'acquis';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignKPIs {
  production_tonnes: number;
  gross_revenue: number;
  total_charges: number;
  gross_margin: number;
  margin_per_ha: number;
  break_even_price: number;
  break_even_yield: number;
}

export interface PriceScenario {
  variation_pct: number;
  price: number;
  revenue: number;
  margin: number;
  margin_delta: number;
}

export interface YieldScenario {
  label: string;
  yield_qtx_ha: number;
  production_tonnes: number;
  revenue: number;
  margin: number;
  impact: number;
}

export interface MonthlyFlow {
  month: number;
  label: string;
  inflows: number;
  outflows: number;
  net: number;
  cumulative: number;
}

export function computeCampaignKPIs(campaign: AgriCampaign, charges: AgriCampaignCharge[]): CampaignKPIs {
  const production_tonnes = (campaign.surface_ha * campaign.target_yield_qtx_ha) / 10;
  const gross_revenue = production_tonnes * campaign.target_price_eur_t;
  const total_charges = charges.reduce((s, c) => s + c.amount, 0);
  const gross_margin = gross_revenue - total_charges;
  const margin_per_ha = campaign.surface_ha > 0 ? gross_margin / campaign.surface_ha : 0;
  const break_even_price = production_tonnes > 0 ? total_charges / production_tonnes : 0;
  const break_even_yield = campaign.target_price_eur_t > 0
    ? (total_charges / (campaign.surface_ha * campaign.target_price_eur_t / 10))
    : 0;

  return { production_tonnes, gross_revenue, total_charges, gross_margin, margin_per_ha, break_even_price, break_even_yield };
}

export function computePriceScenarios(campaign: AgriCampaign, charges: AgriCampaignCharge[]): PriceScenario[] {
  const production_tonnes = (campaign.surface_ha * campaign.target_yield_qtx_ha) / 10;
  const total_charges = charges.reduce((s, c) => s + c.amount, 0);
  const baseMargin = production_tonnes * campaign.target_price_eur_t - total_charges;

  return [-20, -10, 0, 10, 20].map(variation_pct => {
    const price = campaign.target_price_eur_t * (1 + variation_pct / 100);
    const revenue = production_tonnes * price;
    const margin = revenue - total_charges;
    return { variation_pct, price, revenue, margin, margin_delta: margin - baseMargin };
  });
}

export function computeYieldScenarios(campaign: AgriCampaign, charges: AgriCampaignCharge[]): YieldScenario[] {
  const total_charges = charges.reduce((s, c) => s + c.amount, 0);
  const baseMargin = (campaign.surface_ha * campaign.target_yield_qtx_ha / 10) * campaign.target_price_eur_t - total_charges;

  return [
    { label: 'Nominal', pct: 0 },
    { label: '-20% rendement', pct: -20 },
    { label: '-40% rendement', pct: -40 },
  ].map(({ label, pct }) => {
    const yield_qtx_ha = campaign.target_yield_qtx_ha * (1 + pct / 100);
    const production_tonnes = (campaign.surface_ha * yield_qtx_ha) / 10;
    const revenue = production_tonnes * campaign.target_price_eur_t;
    const margin = revenue - total_charges;
    return { label, yield_qtx_ha, production_tonnes, revenue, margin, impact: margin - baseMargin };
  });
}

export function computeMonthlyLoan(principal: number, years: number, annualRate = 0.035): number {
  if (principal <= 0 || years <= 0) return 0;
  const monthlyRate = annualRate / 12;
  const n = years * 12;
  return (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
}

export function getMonthlyPatternWeights(pattern: MonthlyPattern): number[] {
  if (pattern === 'winter_heavy') {
    return [2.5, 2.5, 1.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5];
  }
  return Array(12).fill(1);
}

export function distributeAnnualAmount(annual: number, pattern: MonthlyPattern): number[] {
  const weights = getMonthlyPatternWeights(pattern);
  const total = weights.reduce((s, w) => s + w, 0);
  return weights.map(w => (annual * w) / total);
}

export const CROP_TYPE_LABELS: Record<CropType, string> = {
  ble: 'Blé',
  mais: 'Maïs',
  colza: 'Colza',
  tournesol: 'Tournesol',
  orge: 'Orge',
  autre: 'Autre',
};

export const LIVESTOCK_TYPE_LABELS: Record<LivestockType, string> = {
  bovin_viande: 'Bovin viande',
  bovin_lait: 'Bovin lait',
  porcin: 'Porcin',
  ovin: 'Ovin',
  caprin: 'Caprin',
  avicole: 'Avicole',
  autre: 'Autre',
};

export const SUBSIDY_TYPE_LABELS: Record<SubsidyType, string> = {
  pac: 'PAC / DPB',
  maec: 'MAEC',
  aide_regionale: 'Aide régionale',
  autre: 'Autre',
};

export function formatEur(v: number): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
}

export function formatNum(v: number, decimals = 0): string {
  return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: decimals }).format(v);
}

export const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
